import { useChainId } from "lib/chains";
import useSWR from "swr";
import { useWeb3React } from "@web3-react/core";
import { useLiquidityEventContract } from "pages/Event/hooks/blockchain/contracts/useLiquidityEventContract";
import { getGmxGraphClient } from "../../../../lib/subgraph";
import { ApolloQueryResult, gql } from "@apollo/client";
import { bigNumberify, expandDecimals, parseValue } from "../../../../lib/numbers";
import { BigNumber } from "ethers";
import { useTokenPrices } from "../../../../lib/api/useTokenPrices";

type OverviewInfo = {
  totalCommitment: BigNumber;
  numberOfCommitments: number;
  totalRewards: BigNumber;
  totalRewardsInUsd: BigNumber;
};

type VestScheduleInfos = {
  allocated: string;
  isDestroyed: boolean;
  paidAmount: string;
  vestInfo: { id: string };
};

export const useEventUserInfoQuery = () => {
  const { chainId } = useChainId();
  const { active, account } = useWeb3React();
  const { price } = useTokenPrices("PALM");
  const eventContract = useLiquidityEventContract();

  async function fetcher(args) {
    const [, , account] = args;
    const query = gql(`
      query vestingEventInfoByUser($account: String!) {
        vestScheduleInfos(where: {vestInfo_: { id: $account }, isDestroyed: false}) {
          allocated
          isDestroyed
          paidAmount
          vestInfo {
            id
          }
        }
      }`);

    const fetchUserInfo = async () => {
      const [purchased, destroyed] = await eventContract.userInfo(account);
      return {
        purchased,
        destroyed,
      };
    };
    const fetchOverview = async () => {
      const { data }: ApolloQueryResult<{ vestScheduleInfos: VestScheduleInfos[] }> = await getGmxGraphClient(
        chainId
      )?.query({
        query,
        variables: { account: (account || "").toLowerCase() },
        fetchPolicy: "network-only",
      });
      return data.vestScheduleInfos.reduce<OverviewInfo>(
        (acc, item) => ({
          totalCommitment: bigNumberify(item.paidAmount)?.add(acc.totalCommitment) || acc.totalCommitment,
          numberOfCommitments: acc.numberOfCommitments + 1,
          totalRewards: bigNumberify(item.allocated)?.add(acc.totalRewards) || acc.totalRewards,
          totalRewardsInUsd: acc.totalRewardsInUsd.add(
            bigNumberify(item.allocated)
              ?.mul(parseValue(price.toString(), 4) || BigNumber.from(0))
              .div(expandDecimals(1, 4)) || acc.totalRewardsInUsd
          ),
        }),
        {
          numberOfCommitments: 0,
          totalRewardsInUsd: BigNumber.from(0),
          totalRewards: BigNumber.from(0),
          totalCommitment: BigNumber.from(0),
        }
      );
    };

    const [userInfo, overviewInfo] = await Promise.all([fetchUserInfo(), fetchOverview()]);
    return {
      userInfo,
      overviewInfo,
    };
  }

  const swr = useSWR<Awaited<ReturnType<typeof fetcher>>>(
    account ? [`LiquidityEvent:user-info:${active}`, chainId, account] : null,
    fetcher,
    {
      refreshInterval: 10 * 1000,
    }
  );
  return swr;
};
